import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TourStep } from '@/types/help-tour';
import './help-tour.css';

interface TourOverlayProps {
  steps: TourStep[];
  stepIdx: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const POP_W = 320;
const POP_H = 220;
const POP_MARGIN = 16;

/** Bottom edge of AppBar + brand stripe — popover sits just below chrome. */
function getBelowChromeTop(fallback = 72): number {
  const appbar = document.querySelector('.appbar');
  const stripe = document.querySelector('.brand-stripe');
  let bottom = fallback;
  if (appbar) {
    bottom = appbar.getBoundingClientRect().bottom;
  }
  if (stripe) {
    bottom = Math.max(bottom, stripe.getBoundingClientRect().bottom);
  }
  return bottom;
}

function horizontalCenterLeft(popW: number, margin: number): number {
  return Math.max(margin, (window.innerWidth - popW) / 2);
}

function isViewportTopChrome(rect: DOMRect): boolean {
  return rect.top <= POP_MARGIN && rect.height <= 200;
}

/** Near the top of the page but below AppBar — skip scroll-into-view (e.g. breadcrumb). */
function isNearTopNavBand(rect: DOMRect): boolean {
  return rect.top < 120 && rect.height <= 80;
}

function toSpotlightRect(rect: DOMRect): SpotlightRect {
  const pad = 8;
  const top = Math.max(0, rect.top - pad);
  const left = Math.max(0, rect.left - pad);
  const right = Math.min(window.innerWidth, rect.right + pad);
  const bottom = Math.min(window.innerHeight, rect.bottom + pad);
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top)
  };
}

function unionBoundingRect(rects: DOMRect[]): DOMRect | null {
  if (rects.length === 0) {
    return null;
  }
  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
}

/** Union bounding box of the first N visible direct children. */
function getFirstChildrenBoundingRect(container: Element, count: number): DOMRect {
  const children = Array.from(container.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && child.getBoundingClientRect().height > 0
  );
  if (children.length === 0) {
    return container.getBoundingClientRect();
  }
  const rects = children.slice(0, count).map((child) => child.getBoundingClientRect());
  return unionBoundingRect(rects) ?? container.getBoundingClientRect();
}

/** Union bounding box of grid children sharing the first row's top edge. */
function getFirstRowBoundingRect(container: Element): DOMRect {
  const children = Array.from(container.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && child.getBoundingClientRect().height > 0
  );
  if (children.length === 0) {
    return container.getBoundingClientRect();
  }
  const firstTop = children[0].getBoundingClientRect().top;
  const rowTolerance = 8;
  const firstRowRects = children
    .filter((child) => Math.abs(child.getBoundingClientRect().top - firstTop) <= rowTolerance)
    .map((child) => child.getBoundingClientRect());
  return unionBoundingRect(firstRowRects) ?? container.getBoundingClientRect();
}

const TourOverlay: React.FC<TourOverlayProps> = ({ steps, stepIdx, onNext, onPrev, onClose }) => {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const step = steps[stepIdx];

  useEffect(() => {
    if (!step) {
      setRect(null);
      return undefined;
    }

    let raf1 = 0;
    let raf2 = 0;
    let retry = 0;
    let scrolledForStep = false;

    const measure = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r =
          step.spotlightChildCount != null
            ? getFirstChildrenBoundingRect(el, step.spotlightChildCount)
            : step.spotlightFirstRow
              ? getFirstRowBoundingRect(el)
              : el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0 && retry < 20) {
          retry += 1;
          raf1 = requestAnimationFrame(measure);
          return;
        }
        if (!scrolledForStep && !isViewportTopChrome(r) && !isNearTopNavBand(r)) {
          const offTop = r.top < 80;
          const offBottom = r.bottom > window.innerHeight - 80;
          if (offTop || offBottom) {
            scrolledForStep = true;
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return;
          }
        }
        setRect(toSpotlightRect(r));
      } else if (retry < 20) {
        retry += 1;
        raf1 = requestAnimationFrame(measure);
      } else {
        setRect(null);
      }
    };

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [stepIdx, step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onNext, onPrev, onClose]);

  if (!step) return null;

  const popW = POP_W;
  const popH = POP_H;
  const margin = POP_MARGIN;
  let popTop = getBelowChromeTop() + margin;
  let popLeft = horizontalCenterLeft(popW, margin);

  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const topChrome = rect.top <= POP_MARGIN && rect.height <= 200;
    if (spaceBelow > popH + margin) {
      popTop = rect.top + rect.height + margin;
    } else if (spaceAbove > popH + margin) {
      popTop = rect.top - popH - margin;
    } else {
      popTop = Math.max(margin, (window.innerHeight - popH) / 2);
    }
    popLeft = topChrome ? horizontalCenterLeft(popW, margin) : rect.left + rect.width / 2 - popW / 2;
    popLeft = Math.max(margin, Math.min(window.innerWidth - popW - margin, popLeft));
    popTop = Math.max(margin, Math.min(window.innerHeight - popH - margin, popTop));
  }

  const isLast = stepIdx === steps.length - 1;
  const isFirst = stepIdx === 0;

  return (
    <div className="tour">
      <div className="tour__scrim" onClick={onClose} role="presentation" />
      {rect ? (
        <div
          className="tour__spot"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
        />
      ) : null}
      <div className="tour__pop" style={{ top: popTop, left: popLeft }}>
        <div className="tour__pop-body">
          <div className="tour__step-num">
            Step {stepIdx + 1} of {steps.length}
          </div>
          <h3 className="tour__title">{step.title}</h3>
          <p className="tour__copy">{step.body}</p>
        </div>
        <div className="tour__pop-foot">
          <div className="tour__dots">
            {steps.map((_, i) => (
              <span key={i} className={`tour__dot${i === stepIdx ? ' tour__dot--active' : ''}`} />
            ))}
          </div>
          <TourNav isFirst={isFirst} isLast={isLast} onClose={onClose} onPrev={onPrev} onNext={onNext} />
        </div>
      </div>
    </div>
  );
};

function TourNav({
  isFirst,
  isLast,
  onClose,
  onPrev,
  onNext
}: {
  isFirst: boolean;
  isLast: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  return (
    <div className="tour__nav">
      <button type="button" className="tour__skip" onClick={onClose}>
        {isLast ? tCommon('done') : t('actions.skip')}
      </button>
      {!isFirst ? (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onPrev}>
          {tCommon('back')}
        </button>
      ) : null}
      {!isLast ? (
        <button type="button" className="btn btn--primary btn--sm" onClick={onNext}>
          {tCommon('next')} →
        </button>
      ) : (
        <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
          {t('actions.finish')}
        </button>
      )}
    </div>
  );
}

export default TourOverlay;
